<template>
	<section v-if="photos" ref="el" data-component="club-social-photos" data-section-intersect="light">
		<div class="inner">
			<div class="photos-container">
				<PhotoSpiralGrid ref="photoGridEl" :photos="photos" class="photos-grid" />
			</div>
			<div class="row title-row align-middle">
				<div class="xxlarge-16 col">
					<RollingTitle :label="title" :trigger-element="el" :repeat="rotationRef" class="title" />
					<span ref="subtitleEl" class="-manuscript-2 -uppercase subtitle">{{ subtitle }}</span>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
import { gsap, ScrollTrigger } from 'gsap/all'
import.meta.client && gsap.registerPlugin(ScrollTrigger)

import { useTitleAnimation } from '~/composables'
import { useScreen } from 'vue-screen'

import { breakpoints } from '~/config'

import { RollingTitle } from '~/components/primitives/'

import PhotoSpiralGrid from './components/PhotoSpiralGrid.vue'

const props = defineProps({
	title: { type: String, default: '' },
	subtitle: { type: String, default: '' },
	photos: { type: [Boolean, Array], default: false },
})

const context = inject('pageContext')

const el = ref(null)
const titleRef = ref(null)
const subtitleEl = ref(null)
const photoGridEl = ref(null)
const rotationRef = ref(2)
const screen = useScreen()

const mm = gsap.matchMedia()
let observer

useTitleAnimation(titleRef)
const animateSubtitle = useTypeWrite({
	element: subtitleEl,
	types: 'chars',
	paused: true,
})

watch(screen, () => (rotationRef.value = screen.width > breakpoints.medium ? 1 : 1))

const setup = () => {
	rotationRef.value = screen.width > breakpoints.medium ? 1 : 1
}

const init = () => {
	mm.add(`(min-width: ${breakpoints.medium}px)`, () => {
		observer = ScrollTrigger.create({
			trigger: el.value,
			start: 'top center',
			onEnter: () => {
				photoGridEl.value.play()
				animateSubtitle.animateChars()
				observer.kill()
			},
		})

		return () => {
			kill()
		}
	})

	mm.add(`(max-width: ${breakpoints.medium - 1}px)`, () => {
		observer = ScrollTrigger.create({
			trigger: el.value,
			start: 'top center',
			onEnter: () => {
				photoGridEl.value.play()
				animateSubtitle.animateChars()
				observer.kill()
			},

			start: 'top bottom',
		})
	})
}
const kill = () => {
	observer?.kill()
}

onBeforeUnmount(() => kill())
onMounted(() => {
	if (!props.photos) return
	context.$page.loader.loaded.then(() => setup())
	context.$page.loader.ready.then(() => init())
})
</script>

<style lang="scss" scoped>
[data-component='club-social-photos'] {
	position: relative;

	display: block;
	height: 120svh;
	background-color: var(--color-darkBrown);
	color: var(--color-yellow);
	width: 100%;

	z-index: 2;

	@include max-screen($small) {
		height: 70vh;
	}

	.inner {
		position: absolute;
		top: 0;

		display: flex;

		width: var(--computed-100vw);
	}
	.photos-container {
		position: relative;

		width: 100%;
		height: 120vh;

		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;

		@include max-screen($small) {
			height: 70vh;
		}
	}

	.title-row {
		position: absolute;
		top: 0;
		left: 0;

		width: 100%;
		height: 100%;

		.col {
			text-align: center;
		}

		.title {
			font-size: rs(270);

			:deep(.label) {
				font-size: inherit;
			}
		}

		.subtitle {
			position: relative;
			top: -0.5em;

			display: inline-block;
			white-space: nowrap;
			line-height: 0.2em;

			color: var(--color-white);
			opacity: 0.9;
		}
	}
}
</style>
